import { useState, useCallback, useRef, useEffect } from 'react';

interface UseCountUpOptions {
  duration?: number;
  startValue?: number;
}

interface UseCountUpReturn {
  value: number;
  start: () => void;
}

function easeOutQuart(t: number): number {
  return 1 - Math.pow(1 - t, 4);
}

export function useCountUp(
  targetValue: number,
  options: UseCountUpOptions = {}
): UseCountUpReturn {
  const { duration = 800, startValue = 0 } = options;
  const [value, setValue] = useState(startValue);
  const animationRef = useRef<number | null>(null);
  const previousTargetRef = useRef<number>(targetValue);
  const currentValueRef = useRef<number>(startValue);

  const start = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const startTime = performance.now();
    const from = currentValueRef.current;
    const to = targetValue;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutQuart(progress);
      const currentValue = Math.round(from + (to - from) * easedProgress);

      setValue(currentValue);
      currentValueRef.current = currentValue;

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  }, [targetValue, duration]);

  // Re-trigger animation when target value changes
  useEffect(() => {
    if (previousTargetRef.current !== targetValue) {
      start();
    }
    previousTargetRef.current = targetValue;
  }, [targetValue, start]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return { value, start };
}
